import { useState, useEffect, useCallback } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { useHistory } from "react-router-dom"
import { Form } from "react-bootstrap"
import Helmet from "react-helmet"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import classNames from "classnames"
import { clearOnLogout } from "apolloClient"
import { errorMessages } from "components/shared/ErrorToast/constants"
import Nav from "components/shared/Nav"
import Button from "components/shared/Button"
import ErrorToast from "components/shared/ErrorToast"
import FullPageLoader from "components/shared/FullPageLoader"
import PhoneNumberInput from "components/shared/PhoneNumberInput"
import {
  useForceAuthEffect,
  useVerificationRedirectEffect
} from "components/Login/hooks"
import { Account } from "components/Login/operations"
import warningIcon from "../../shared/_assets/warning-icon.svg"
import { PhoneNumberVerification, Logout } from "./operations"

import styles from "./SmsVerify.module.scss"

const DELIVERY_METHODS = [
  {
    value: "sms",
    title: "Text message",
    description: "Recommended for U.S. and Canada numbers"
  },
  {
    value: "whatsapp",
    title: "WhatsApp",
    description: "Supports most international numbers"
  }
]

const SmsVerify = () => {
  useForceAuthEffect({ authUrl: "/login" })
  useVerificationRedirectEffect()
  const history = useHistory()
  const [deliveryMethod, setDeliveryMethod] = useState("sms")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [validated, setValidated] = useState(false)
  const [phoneNumberError, setPhoneNumberError] = useState("")

  // Fetch account data to prefill phone number
  const { data: accountData, loading: accountLoading } = useQuery(Account, {
    variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
  })

  const user = get(accountData, "me")

  // Prefill phone number from account data
  useEffect(() => {
    if (user?.mobilePhoneNumber && !phoneNumber && !accountLoading) {
      setPhoneNumber(
        `${user.mobileCountryPrefix || "+1"}${user.mobilePhoneNumber}`
      )
    }
  }, [user, accountLoading])

  const handlePhoneNumberChange = (newPhoneNumber) => {
    setPhoneNumber(newPhoneNumber)
    // Clear errors when user starts typing
    if (phoneNumberError) {
      setPhoneNumberError("")
    }
  }

  const [phoneNumberVerification, { loading }] = useMutation(
    PhoneNumberVerification,
    {
      refetchQueries: [
        {
          query: Account,
          variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
        }
      ],
      onCompleted: ({ phoneNumberVerification: verificationResult }) => {
        const { success, errors } = verificationResult

        if (success) {
          history.replace("/verify-code")
        } else if (errors && errors.length > 0) {
          switch (errors[0]) {
            case "blacklisted_phone_number":
              history.replace("/sms-blocked")
              break
            default:
              setPhoneNumberError(errorMessages[errors[0]])
          }
        }
      }
    }
  )

  const [logoutUser, { client }] = useMutation(Logout)

  const logout = useCallback(() => {
    clearOnLogout(client)
    logoutUser({
      variables: {
        input: {}
      }
    })
    history.push("/login")
  }, [logoutUser])

  const handleSubmit = (e) => {
    e.preventDefault()

    phoneNumberVerification({
      variables: {
        input: {
          phoneNumber,
          verificationChannel: deliveryMethod
        }
      }
    })
    setValidated(true)
  }

  if (loading || accountLoading) {
    return <FullPageLoader />
  }

  return (
    <div className="h-100">
      <Helmet>
        <title>Verify your phone number</title>
      </Helmet>
      <Nav />
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>Verify Your Account</h2>
          <p className={styles.subtitle}>
            We&apos;ll send a one-time code to your phone number to confirm
            it&apos;s really you.
          </p>
        </div>

        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className={styles.formContainer}
        >
          <div>
            <h5 className={styles.formTitle}>Choose Delivery Method</h5>

            {DELIVERY_METHODS.map((method) => (
              <div
                key={method.value}
                className={classNames(styles.formOption, {
                  [styles.selected]: deliveryMethod === method.value
                })}
              >
                <div className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value={method.value}
                    checked={deliveryMethod === method.value}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                    className={styles.radioInput}
                  />
                  <div className={styles.radioContent}>
                    <h5 className={styles.formOptionTitle}>{method.title}</h5>
                    <p className={styles.formOptionDescription}>
                      {method.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className={styles.phoneNumberInputContainer}>
              <label htmlFor="phoneNumber" className={styles.formTitle}>
                Phone Number
              </label>
              <PhoneNumberInput
                id="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                displayError={validated && !isEmpty(phoneNumberError)}
              />
              {validated && phoneNumberError && (
                <div id="phone-error" className={styles.errorContainer}>
                  <img src={warningIcon} alt="error" />
                  <div className={styles.errorMessage}>{phoneNumberError}</div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <Button
              type="submit"
              primary
              disabled={!phoneNumber || !isEmpty(phoneNumberError)}
            >
              Continue
            </Button>
            <Button outline onClick={logout}>
              Log Out
            </Button>
          </div>
        </Form>
      </div>
      <ErrorToast />
    </div>
  )
}

export default SmsVerify
